import { LOCAL_HISTORY_LIMIT } from "./constants";
import type { TryOnJob } from "./types";

const DB_NAME = "meijia-tryon";
const DB_VERSION = 1;
const JOB_STORE = "jobs";

let dbPromise: Promise<IDBDatabase> | null = null;

export function listJobs(): Promise<TryOnJob[]> {
  return withStore("readonly", (store) => requestToPromise<TryOnJob[]>(store.getAll())).then((jobs) =>
    jobs.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  );
}

export function saveJob(job: TryOnJob): Promise<void> {
  return withStore("readwrite", (store) => requestToPromise(store.put(job))).then(() =>
    pruneJobs(LOCAL_HISTORY_LIMIT)
  );
}

export function deleteJob(id: string): Promise<void> {
  return withStore("readwrite", (store) => requestToPromise(store.delete(id))).then(() => undefined);
}

export function clearJobs(): Promise<void> {
  return withStore("readwrite", (store) => requestToPromise(store.clear())).then(() => undefined);
}

export async function pruneJobs(limit = LOCAL_HISTORY_LIMIT): Promise<void> {
  const jobs = await listJobs();
  const stale = jobs.slice(limit);
  await Promise.all(stale.map((job) => deleteJob(job.id)));
}

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(JOB_STORE)) {
          const store = db.createObjectStore(JOB_STORE, { keyPath: "id" });
          store.createIndex("createdAt", "createdAt");
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("打开本地缓存失败。"));
    });
  }

  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(JOB_STORE, mode);
  const store = transaction.objectStore(JOB_STORE);
  const done = transactionDone(transaction);
  const result = await callback(store);
  await done;
  return result;
}

function requestToPromise<T = unknown>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("本地缓存操作失败。"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("本地缓存事务失败。"));
    transaction.onabort = () => reject(transaction.error ?? new Error("本地缓存事务中断。"));
  });
}

export function resetStorageForTests(): void {
  dbPromise = null;
}
